import React, { useMemo } from 'react';
import { Button, Modal, Typography } from '@attentive-platform/stem-ui';

import { ConfirmLinkModalStyles as S } from './styles';
import { SOP_MODE_LABELS } from '../../../Constants/sopMode';
import { formatLocaleDate } from '../../../Utils/HelperFunctions';

const EFFECTS = [
  'The company moves off free-form onto this scope record.',
  'New takeoffs render scope cards from this record and pin a snapshot at submit.',
  'The enterprise-conversion gate is satisfied.'
];

const summarizeTrades = record => {
  const trades = Array.isArray(record?.trades_in_scope) ? record.trades_in_scope : [];
  return trades
    .map(t => {
      const sections = (Array.isArray(t.sections) ? t.sections : [])
        .map(s => ({
          id: s.section_id || s.id,
          name: s.section_name || s.name || '—',
          scopeCount: (s.assigned_scopes || s.scopes || []).length
        }))
        .filter(s => s.scopeCount > 0);
      return {
        id: t.trade_id || t.id,
        name: t.trade_name || t.name || '—',
        sections
      };
    })
    .filter(t => t.sections.length > 0);
};

const ConfirmLinkModal = ({
  show,
  onClose,
  onConfirm,
  submission,
  company,
  submitting,
  loadingScope
}) => {
  const picked = submission?.company || {};
  const scopeMode = submission?.scope_mode;
  const trades = useMemo(() => summarizeTrades(submission?.scopeRecord), [submission?.scopeRecord]);
  const sectionCount = trades.reduce((n, t) => n + t.sections.length, 0);

  if (!submission) return null;

  const facts = [
    { label: 'Company', value: picked.name || company || '—' },
    {
      label: 'Customer email',
      value: picked.customer_email || submission.customer_email || '—'
    },
    { label: 'Card id', value: submission.card_id || '—' },
    { label: 'Registered', value: formatLocaleDate(picked.registered_on) || '—' },
    { label: 'Current scope mode', value: SOP_MODE_LABELS[scopeMode] || scopeMode || '—' },
    {
      label: 'Trades',
      value: loadingScope
        ? 'Loading…'
        : trades.length
          ? `${trades.length} trade${trades.length === 1 ? '' : 's'}`
          : '—'
    },
    {
      label: 'Sections',
      value: loadingScope
        ? 'Loading…'
        : sectionCount
          ? `${sectionCount} section${sectionCount === 1 ? '' : 's'}`
          : '—'
    }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      heading={<span className='pl-4'>Link scope record</span>}
      content={
        <S.ContentWrap>
          <S.Heading>
            This submission becomes {company || 'the company'}&apos;s scope of record
          </S.Heading>
          <S.FactsBox>
            {facts.map(f => (
              <S.FactRow key={f.label}>
                <Typography variant='body4' color='#808080'>
                  {f.label}
                </Typography>
                <Typography variant='body4' sx={{ fontWeight: 500, textAlign: 'right' }}>
                  {f.value}
                </Typography>
              </S.FactRow>
            ))}
          </S.FactsBox>
          {!loadingScope && trades.length > 0 && (
            <S.FactsBox>
              {trades.map(t => (
                <S.FactRow key={t.id || t.name}>
                  <Typography variant='body4' sx={{ fontWeight: 500 }}>
                    {t.name}
                  </Typography>
                  <Typography variant='body4' color='#808080' sx={{ textAlign: 'right' }}>
                    {t.sections.length
                      ? t.sections.map(s => s.name).join(', ')
                      : 'No sections'}
                  </Typography>
                </S.FactRow>
              ))}
            </S.FactsBox>
          )}
          <S.Effects>
            {EFFECTS.map(text => (
              <S.BulletRow key={text}>
                <S.BulletDot />
                <Typography variant='body3' color='#4D4D4D' sx={{ lineHeight: 1.55 }}>
                  {text}
                </Typography>
              </S.BulletRow>
            ))}
          </S.Effects>
          <Typography variant='body4' color='#808080'>
            Recorded in the activity log with your name.
          </Typography>
          <S.Actions>
            <Button variant='outlined' color='primary' size='medium' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='medium'
              loading={submitting}
              disabled={loadingScope}
              onClick={onConfirm}>
              Link scope record
            </Button>
          </S.Actions>
        </S.ContentWrap>
      }
    />
  );
};

export default ConfirmLinkModal;
